import { Environment } from 'environment.ts'
import { get, post } from 'Repository/index.js'
import type { Observable } from 'rxjs'
import { map, mergeMap, of } from 'rxjs'
import type { ITokenInfoOrganizationDetail, ITokenInfoResponse, IUser } from 'Model/index.js'
import type { CommercialType, SubscriptionStatus } from 'Model/User/IUser.js'
import type { IUserWithOverride } from 'Model/User/organizationOverride.js'
import { Licenses, OrganizationType } from 'Model/index.js'
import type { OrganizationOverrideInfo } from '../../Generated/OrganizationOverrideInfo.ts'
import type { OrganizationOverrideRequest } from '../../Generated/OrganizationOverrideRequest.ts'
import type { OrganizationOverrideResponse } from '../../Generated/OrganizationOverrideResponse.ts'

const VALID_SUB_STATUS = new Set<string>(['active', 'non_renewing', 'cancelled', 'paused', 'deleted'])
const VALID_COMMERCIAL = new Set<string>(['COMMERCIAL', 'ACADEMIC'])

/**
 * Under an admin organization override the `organization` claim still describes the *operator's* org,
 * so every licence read has to come from `spcfy_effective_organization` instead.
 */
export interface ITokenInfoWithOverride extends ITokenInfoResponse {
  spcfy_effective_organization?: Record<string, ITokenInfoOrganizationDetail>
  spcfy_organization_override?: OrganizationOverrideInfo
}

export const acceptInvitation = (data: { invitationId: string; password: string }) => {
  return post<typeof data, Record<string, never>>(`${Environment.API_URL}/v1/user/laboratory/invitation/accept`, data)
}

export const mapTokenInfoToUser = (response: ITokenInfoWithOverride): IUserWithOverride => {
  const showAdminUx = booleanAttr(response.show_admin_ux)
  const override = response.spcfy_organization_override
  const [organizationName, organization] = effectiveOrg(response)
  const organizationId =
    showAdminUx && response.roles?.includes('global-administrator') && response.organization_override_id
      ? response.organization_override_id
      : organization.id

  return {
    ...response,
    id: response.sub,
    firstName: response.given_name,
    lastName: response.family_name,
    email: response.email,
    org: {
      name: organizationName,
      id: organizationId,
      type: firstOrNull(organization['org_type']) ?? OrganizationType.LABORATORY,
      isInternal: booleanAttr(organization['org_internal']),
      isPublic: organization['org_is_public'] ?? false,
      defaultSampleConfigurationType: firstOrNull(organization['org::sample_config']) ?? 'SINGLE_SAMPLE_PAIRS',
      defaultRedlistMode: firstOrNull(organization['org::default_redlist_mode']) ?? 'NONE',
      defaultProcessingType: firstOrNull(organization['org::default_processing_type']) ?? 'METABARCODING',
      isPending: (organization['org_pending'] ?? [])[0] === 'true',
      lastPaymentIssueAt: firstOrNull(organization.chargebee_last_payment_issue_at) ?? undefined,
      licenses: organization.licenses?.flatMap((license) => ({
        tier: license,
        featureAccesses: Licenses[license].featureAccesses
      })) || [Licenses.BASE],
      isBillingMissing: booleanAttr(organization.org_billing_missing),
      chargebeeCustomerId: firstOrNull(organization.chargebee_customer_id) ?? undefined,
      chargebeePlanId: firstOrNull(organization.chargebee_plan_id) ?? undefined,
      chargebeeSubscriptionId: firstOrNull(organization.chargebee_subscription_id) ?? undefined,
      chargebeeSubscriptionStatus: validateClaim<SubscriptionStatus>(
        firstOrNull(organization.chargebee_subscription_status),
        VALID_SUB_STATUS
      ),
      chargebeeTrialEndsAt: firstOrNull(organization.chargebee_trial_ends_at) ?? undefined,
      commercialType: validateClaim<CommercialType>(firstOrNull(organization.org_commercial_type), VALID_COMMERCIAL),
      chargebeeEntitlements: organization.chargebee_entitlements ?? undefined
    },
    showAdminUx,
    organizationOverrideId: response.organization_override_id,
    organizationOverride: override,
    authServer: response.iss
  } as IUserWithOverride
}

export const retrieveMe = (): Observable<IUser> => {
  return get<object, ITokenInfoWithOverride>(`${Environment.API_URL}/token_info`).pipe(
    mergeMap((response) => of(mapTokenInfoToUser(response)))
  )
}

export const getOrganizationOverride = (): Observable<OrganizationOverrideResponse> => {
  return get<object, OrganizationOverrideResponse>(`${Environment.API_URL}/api/admin/markus/organization-override`)
}

export const setOrganizationOverride = (request: OrganizationOverrideRequest): Observable<OrganizationOverrideResponse> => {
  return post<OrganizationOverrideRequest, OrganizationOverrideResponse>(
    `${Environment.API_URL}/api/admin/markus/organization-override`,
    request
  )
}

export interface IRefreshKcTokenResponse {
  refreshed: boolean
}

export const refreshKcToken = (): Observable<IRefreshKcTokenResponse> => {
  return post<Record<string, never>, IRefreshKcTokenResponse>(`${Environment.API_URL}/api/admin/markus/refresh-kc-token`, {})
}

export const refreshChargebeeSession = (): Observable<void> => {
  return get<object, unknown>(`${Environment.API_URL}/v1/chargebee/refresh-session`).pipe(map(() => undefined))
}

// Keycloak attributes reach /token_info as raw String claims, so `"false"` is truthy until coerced.
const booleanAttr = (attr?: string | string[] | null) =>
  Array.isArray(attr) ? attr.includes('true') : attr === 'true'

const effectiveOrg = (response: ITokenInfoWithOverride): [string, ITokenInfoOrganizationDetail] => {
  const claim = response.spcfy_effective_organization ?? response.organization
  const name = Object.keys(claim)[0]
  return [name, claim[name]]
}

function firstOrNull<T>(values?: T[]): T | null {
  if (values !== undefined && values.length > 0) {
    return values[0]
  }
  return null
}

function validateClaim<T extends string>(value: string | null, allowed: Set<string>): T | undefined {
  return value && allowed.has(value) ? (value as T) : undefined
}
